import { EventEmitter, Injectable, inject, signal } from '@angular/core';
import { keys } from '@clubhub/constants';
import { CriteriaSteps } from '@clubhub/ignite/models/criteria/criteria-steps';
import { PropertyModelRequest } from '@clubhub/ignite/models/property';
import { CriteriaAnswers, CriteriaFormAnswer, CriteriaFormResult, CriteriaFormResultRequest, GetOutcomesRequest, GetProductsOutcomesRequest, IntuitiveCriteriaPropertyStatus, OutcomeDetailsCardV2 } from '@clubhub/ignite/pages/criteria-v2/models';
import { BasketService, BrokerService } from '@clubhub/services';
import { LenderCriteriaSaveRequest, LenderCriteriaTitleSaveRequest } from '@lenderhub//pages/criteria-v2/models';
import { downloadBlob, join2 } from '@shared/helpers';
import { AffordabilityRequest, BdgAffordabilityRequest, BtlAffordabilityRequest, CriteriaType, FlowType, ISimpleGridFilterOperation, Lender, OutcomeDetails, OutcomeDetailsCard, OutcomeResponse, OutcomeResponseOverallResult, OutcomeResults } from '@shared/models';
import { ContextQuestion, ContextQuestions, CriteriaIssue, QuestionType } from '@shared/models/criteria-issue';
import { LenderHubDataService, LendingTypeService } from '@shared/services';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';
import { BehaviorSubject, Observable, finalize, shareReplay, tap } from 'rxjs';
import { AffordabilityService } from '../affordability/affordability.service';
import { IgniteCommonDataService } from '../ignite.store';
import { S365Service } from '../s365.service';
import { SavedSearchCommonDataService } from '../saved-searches/saved-search-common-data.service';

// Minimal shape of PropertySearchService needed here, to avoid a circular file dependency
// (property-search.service.ts imports CriteriaV2Service directly).
interface PropertySearchServiceLike {
  propertySearchRequestViewModel: PropertyModelRequest | null;
  splitOutPropertyCriteria(
    intuitiveOutcomes: OutcomeResponse[] | null,
    criteriaOutcomes: OutcomeResponse[],
    addingProperty?: boolean,
  ): void;
}

@Injectable({
  providedIn: 'root',
})
export class CriteriaV2Service {
  private readonly basketService = inject(BasketService);
  private readonly clubHubDataService = inject(ClubHubDataService);
  private readonly lenderHubDataService = inject(LenderHubDataService);
  private readonly affordabilityService = inject(AffordabilityService);
  private readonly lendingTypeService = inject(LendingTypeService);
  private readonly brokerService = inject(BrokerService);
  private readonly s365Service = inject(S365Service);
  private readonly savedSearchCommonDataService = inject(SavedSearchCommonDataService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);

  public basketItems: CriteriaFormResult[] = [];
  public outcome: OutcomeDetails = { details: [] } as { details: OutcomeResponse[] } as OutcomeDetails;
  private readonly _step = signal<CriteriaSteps>(CriteriaSteps.CriteriaSelect);
  public uiLoading = false;
  public dataLoading = false;
  public lenderCount = 0;
  public criteriaIssues: CriteriaIssue[] = [];
  public outcomeUpdated = new EventEmitter<OutcomeDetails>();
  public showBrokerDetails: boolean = false;
  public lender: Lender | null = null;
  public outcomeDetails: OutcomeDetails = { details: [] } as { details: OutcomeResponse[] } as OutcomeDetails;
  public additionalOutcomeIds: (number | undefined)[] = [];
  public additionalOutcomes: OutcomeResponse[] = [];
  public intuitiveContextCards: OutcomeDetailsCard[] = [];
  public intuitiveOutcomes: OutcomeResponse[] = [];
  public criteriaSearchId: string | null = null;

  public get step(): CriteriaSteps {
    return this._step();
  }

  public set step(value: CriteriaSteps) {
    this._step.set(value);
  }

  public loading$ = new BehaviorSubject(false);

  public criteria: CriteriaIssue[] = [];
  private readonly contextQuestionObservables = new Map<number, Observable<ContextQuestion[]>>();

  public get loading() {
    return this.loading$.value;
  }

  public set loading(value: boolean) {
    this.loading$.next(value);
  }

  public getIssues(lenderTypeId: number): Observable<CriteriaIssue[]> {
    return this.clubHubDataService
      .get<CriteriaIssue[]>(`CriteriaV2/Issues?lendingTypeId=${lenderTypeId}`)
      .pipe(tap(criteria => (this.criteria = criteria)));
  }

  public getContextQuestions(contextId: number): Observable<ContextQuestion[]> | undefined {
    if (!this.contextQuestionObservables.has(contextId)) {
      this.contextQuestionObservables.set(
        contextId,
        this.clubHubDataService
          .get<ContextQuestion[]>(`CriteriaV2/Context/${contextId}/Questions`, { loading: false })
          .pipe(shareReplay(1)),
      );
    }
    return this.contextQuestionObservables.get(contextId);
  }

  public getLenderContextQuestions(contextId: number, lenderId: number): Observable<ContextQuestions> {
    return this.lenderHubDataService.get(`Criteria/Context/${contextId}/Lender/${lenderId}/Questions`);
  }

  public getLiveContextQuestions(contextId: number, lenderId: number): Observable<ContextQuestions> {
    return this.lenderHubDataService.get(`Criteria/Context/${contextId}/Lender/${lenderId}/LiveQuestions`);
  }

  public saveLenderCriteria(body: LenderCriteriaSaveRequest) {
    return this.lenderHubDataService.post('Criteria/LenderCriteria', body);
  }

  public getSearchSynonyms(): Observable<[string, string][]> {
    return this.clubHubDataService.get<[string, string][]>('CriteriaV2/SearchSynonyms');
  }

  public saveLenderCriteriaTitle(request: LenderCriteriaTitleSaveRequest) {
    return this.lenderHubDataService.post('Criteria/LenderCriteriaTitle', request);
  }

  public verifyLenderContext(criteriaContextLendingTypeId: number, lenderId?: number) {
    return this.lenderHubDataService.patch(`Criteria/VerifyContext/${criteriaContextLendingTypeId}/Lender/${lenderId}`, {});
  }

  /**
   * The Meets criteria filter the broker has on the criteria results grid.
   *
   * Held here because the grid keeps its filters inside its own instance, and the criteria tab is re-created
   * every time the broker switches to products or affordability and back - so the filter was lost each time.
   */
  public meetsCriteriaFilter: ISimpleGridFilterOperation | null = null;

  /**
   * Whether the broker's saved Meets criteria preference has been applied on this search.
   *
   * Applied once, on the first showing. After that the filter is the broker's, and coming back to the tab puts
   * back what they had rather than the preference.
   */
  public meetsCriteriaPreferencesApplied = false;

  /** A new search: New search, choosing a tool from a landing page, or a second-charge mortgage type change. */
  public resetParamsToDefault(): void {
    this.basketService.clearBasket();
    this.outcomeDetails.contexts = [];
    this.outcomeDetails.details = [];
    this.meetsCriteriaFilter = null;
    this.meetsCriteriaPreferencesApplied = false;
  }

  public yesLenders(): OutcomeResponseOverallResult[] {
    return this.outcomeDetails.details
      .filter(x => (x.overallResult.outcomeResult as OutcomeResults) === OutcomeResults.Yes)
      .sort((a, b) => a.overallResult.lender.localeCompare(b.overallResult.lender))
      .map(x => x.overallResult);
  }

  public referLenders(): OutcomeResponseOverallResult[] {
    return this.outcomeDetails.details
      .filter(x => (x.overallResult.outcomeResult as OutcomeResults) === OutcomeResults.Refer)
      .sort((a, b) => a.overallResult.lender.localeCompare(b.overallResult.lender))
      .map(x => x.overallResult);
  }

  public noLenders(): OutcomeResponseOverallResult[] {
    return this.outcomeDetails.details
      .filter(x => (x.overallResult.outcomeResult as OutcomeResults) === OutcomeResults.No)
      .sort((a, b) => a.overallResult.lender.localeCompare(b.overallResult.lender))
      .map(x => x.overallResult);
  }

  public getEpcReportPdf(postcode: string, address: string): void {
    this.clubHubDataService.postFileBlob('propertyCriteriaV2/EpcReport', { postcode, address }).subscribe({
      next: blob => downloadBlob(blob, `EPC-${address.replace(/[\s,]+/, '_')}-${postcode}.pdf`),
      error: () => window.open('https://find-energy-certificate.service.gov.uk', '_blank', 'noopener'),
    });
  }

  public workOutCriteriaType(outcome: OutcomeDetailsCard) {
    if (outcome.issue.includes('Intuitive Criteria')) {
      return CriteriaType.Intuitive;
    } else if (outcome.issue.includes('Added Automatically')) {
      return CriteriaType.Property;
    }
    return CriteriaType.Manual;
  }

  public get flowType(): FlowType {
    const flowType = sessionStorage.getItem(keys.flowType);
    if (flowType) {
      return +flowType;
    }

    throw new Error('FlowType not find in session storage');
  }

  public set flowType(flowType: FlowType) {
    sessionStorage.setItem(keys.flowType, flowType.toString());
  }

  public get isAdmin(): boolean {
    return sessionStorage.getItem(keys.isAdmin) === 'true';
  }

  public set isAdmin(value: boolean) {
    sessionStorage.setItem(keys.isAdmin, value.toString());
  }

  public addBasketItem(item: CriteriaFormResult): void {
    this.basketItems.push(item);
    this.basketItems.sort((a, b) => a.criteriaContextName.localeCompare(b.criteriaContextName));
  }

  public get manualCriteria() {
    return this.basketItems.filter(item => item.criteriaType === CriteriaType.Manual);
  }

  public get intuitiveCriteria() {
    return this.basketItems.filter(item => item.criteriaType === CriteriaType.Intuitive);
  }

  public get propertyCriteria() {
    return this.basketItems.filter(item => item.criteriaType === CriteriaType.Property);
  }

  public get hasNonAutoAddedCriteria(): boolean {
    return this.basketItems.filter(item => item.criteriaType === CriteriaType.Manual).length > 0;
  }

  public getLenderOverallResultByLender(lenderName: string): OutcomeResults | undefined {
    const lenderOutcome = this.outcome.details?.find(item => item.overallResult.lender.toUpperCase() === lenderName.toUpperCase());

    if (!lenderOutcome) {
      return;
    }

    const outcomeResult = lenderOutcome.overallResult.outcomeResult;
    switch (outcomeResult) {
      case 'Y':
        return OutcomeResults.Yes;
      case 'N':
        return OutcomeResults.No;
      case 'R':
        return OutcomeResults.Refer;
      default:
        return;
    }
  }

  public getLenderCriteriaByLender(lenderName: string, criteriaType: CriteriaType, property: IntuitiveCriteriaPropertyStatus) {
    const lenderOutcomes = this.outcome.details?.filter(item => item.overallResult.lender.toUpperCase() === lenderName.toUpperCase());

    if (!lenderOutcomes?.length) {
      return;
    }

    const contextResults =
      lenderOutcomes
        ?.flatMap(lenderOutcome => lenderOutcome.contextResults)
        .filter(context => {
          if (property === IntuitiveCriteriaPropertyStatus.Both) {
            return context.criteriaType === criteriaType;
          }
          return context.criteriaType === criteriaType && context.isProperty === (property === IntuitiveCriteriaPropertyStatus.Property);
        }) ?? [];

    return {
      contextResults: contextResults,
      overallResult: lenderOutcomes[0].overallResult,
      outcomeContextCards: lenderOutcomes[0].outcomeContextCards,
      skipList: lenderOutcomes[0].skipList,
    } as OutcomeResponse;
  }

  public removeBasketItem(item: CriteriaFormResult): void {
    const index = this.basketItems.indexOf(item);
    if (index > -1) {
      this.basketItems.splice(index, 1);
    }
  }

  public removeBasketItemById(contextId: number): void {
    this.basketItems = this.basketItems.filter(item => item.criteriaContextId !== contextId);
  }

  public removeBasketItemByContextName(contextName: string): void {
    this.basketItems = this.basketItems.filter(item => item.criteriaContextName !== contextName);
  }

  public removeNotIntuitiveBaskets() {
    this.basketItems = this.intuitiveCriteria;
  }

  public clearBasket(): void {
    this.basketItems = [];
  }

  public clearBasketIntuitive(): void {
    this.basketItems = this.basketItems.filter(item => item.criteriaType !== CriteriaType.Intuitive);
  }

  public clearBasketProperty(): void {
    this.basketItems = this.basketItems.filter(item => item.criteriaType !== CriteriaType.Property);
  }

  public reset(): void {
    this.clearBasket();
    this.step = CriteriaSteps.CriteriaSelect;
    this.lenderCount = 0;
    this.outcome = {} as OutcomeDetails;
  }

  public get outcomeCards(): OutcomeDetailsCardV2[] {
    return this.basketItems.map(item => {
      return {
        issue: item.issue ?? this.getIssueName(item.criteriaContextLendingTypeId) ?? '',
        context: item.criteriaContextName,
        criteriaType: item.criteriaType,
        questionsAndAnswers: item.answers.map(curAnswer => {
          const singleOrOtherAnswer = curAnswer.answerType === QuestionType.SingleSelect
            ? curAnswer.answerName
            : curAnswer.answer.toString();
          return {
            question: curAnswer.questionName,
            answerUnits: curAnswer.answerType === QuestionType.FullRange ? curAnswer.answerUnits : '',
            answer:
              curAnswer.answerType === QuestionType.MultiSelect
                ? join2(
                  curAnswer.answer.map(x => x.name),
                  ', ',
                  ' and ',
                )
                : singleOrOtherAnswer,
          };
        }),
      };
    });
  }

  public getOutcomeType(text: string): CriteriaType {
    if (text.includes('Intuitive Criteria')) {
      return CriteriaType.Intuitive;
    }
    if (text.includes('Added Automatically')) {
      return CriteriaType.Property;
    }
    return CriteriaType.Manual;
  }

  public getIssueName(criteriaContextLendingTypeId: number): string | undefined {
    const criteriaItems = this.criteria ?? [];
    const criteria = criteriaItems.find(x => (x.contexts ?? []).some(y => y.criteriaContextLendingTypeId === criteriaContextLendingTypeId));
    return criteria?.name;
  }

  private createBasketItems(detailsCards: OutcomeDetailsCard[], autoAddedOutcomeIds: number[] | null = []) {
    return detailsCards.map(c => {
      return {
        criteriaContextLendingTypeId: c.criteriaContextLendingTypeId,
        criteriaContextId: c.contextId,
        criteriaContextName: c.context,
        criteriaContextVersionId: c.criteriaContextVersionId,
        issue: c.issue,
        issueId: c.issueId,
        criteriaType: c.outcomeType,
        autoAdded: c.contextId !== undefined && autoAddedOutcomeIds?.includes(c.contextId),
        answers: c.questionsAndAnswers.map(q => {
          if (q.answerType !== QuestionType.MultiSelect) {
            return {
              answerType: q.answerType,
              questionId: q.questionId,
              questionName: q.question,
              answer: q.answerType === QuestionType.SingleSelect ? q.answerId || q.answer : q.answer,
              answerName: q.answerName,
              answerUnits: q.answerUnits,
            } as CriteriaFormAnswer;
          }
          return {
            answerType: q.answerType,
            questionId: q.questionId,
            questionName: q.question,
            answer: q.answer
              ? q.answer?.map((answer: any) => {
                return {
                  id: answer.id,
                  name: answer.name,
                  answerUnits: answer.answerUnits,
                } as CriteriaAnswers;
              })
              : q.answers?.map((answer: any) => {
                return {
                  id: answer.id,
                  name: answer.text,
                  answerUnits: '',
                } as CriteriaAnswers;
              }),
          } as CriteriaFormAnswer;
        }),
      } as CriteriaFormResult;
    });
  }

  public updateBasketItems(
    newDetailsCards: OutcomeDetailsCard[],
    isFirstTimePropertyCheck: boolean = true,
    autoAddedOutcomeIds: number[] | null = [],
  ) {
    const newBasketItems = this.createBasketItems(newDetailsCards, autoAddedOutcomeIds);
    if (!isFirstTimePropertyCheck) {
      this.basketItems = this.basketItems?.filter(x => !newBasketItems.map(n => n.criteriaContextName).includes(x.criteriaContextName));
    }
    this.basketItems = this.basketItems.concat(newBasketItems);
  }

  public setBasketItems(detailsCards: OutcomeDetailsCard[], clearExisting = true, autoAddedOutcomeIds: number[] | null = []) {
    const newItems = this.createBasketItems(detailsCards, autoAddedOutcomeIds);
    this.basketItems = clearExisting ? newItems : [...this.basketItems, ...newItems];
  }

  public getOutcome(
    lenderTypeId: number,
    affordabilitySearchId: string | null,
    propertyRequest: PropertyModelRequest | null,
    affordabilityModelRequest: AffordabilityRequest | BtlAffordabilityRequest | BdgAffordabilityRequest | null = null,
    { scrollToTop = true } = {},
  ): Observable<OutcomeDetails> {
    this.uiLoading = true;
    this.dataLoading = true;
    let hasOutcomeResponse = false;
    const criteriaFormResults = this.getCriteriaFormResultRequests();
    const getOutcomesRequest = {
      criteriaRequests: criteriaFormResults,
      affordabilitySearchId: affordabilitySearchId,
      caseId: this.s365Service.s365Data?.caseId ?? null,
      affordabilityRequest: affordabilityModelRequest,
      brokerFirmDetails: this.brokerService.broker
        ? {
          agencyNumber: this.brokerService.broker?.brokerAgencyNumber,
          fcaNumber: this.brokerService.broker?.brokerFcaNumber,
        }
        : null,
      propertyRequest: propertyRequest,
    } as GetOutcomesRequest;

    return this.clubHubDataService.post<OutcomeDetails>(`CriteriaV2/Outcome/${lenderTypeId}`, getOutcomesRequest).pipe(
      tap((res: OutcomeDetails) => {
        hasOutcomeResponse = true;
        this.step = CriteriaSteps.CriteriaResult;
        this.outcome = res;
        this.lenderCount = res.details.length;
        this.outcomeUpdated.emit(res);
        this.dataLoading = false;
        this.savedSearchCommonDataService.clearSavedSearch();
        setTimeout(() => {
          this.uiLoading = false;
          if (scrollToTop) {
            window.scrollTo(0, 0);
          }
        }, 15000); // if you change the length of this timeout you will need to change it in the css animation too.
      }),
      finalize(() => {
        if (hasOutcomeResponse) {
          return;
        }

        this.dataLoading = false;
        this.uiLoading = false;
      }),
    );
  }

  public getProductsIntuitiveOutcome(
    lenderTypeId: number,
    productsModelRequest: any,
    propertyModelRequest?: PropertyModelRequest | null,
  ): Observable<OutcomeDetails> {
    const criteriaFormResults = this.getCriteriaFormResultRequests();
    const getProductsOutcomesRequest = {
      criteriaRequests: criteriaFormResults,
      productsSearchRequest: productsModelRequest,
      brokerFirmDetails: this.brokerService.broker
        ? {
          agencyNumber: this.brokerService.broker?.brokerAgencyNumber,
          fcaNumber: this.brokerService.broker?.brokerFcaNumber,
        }
        : null,
      propertyCriteriaRequestViewModel: propertyModelRequest,
    } as GetProductsOutcomesRequest;

    return this.clubHubDataService.post<OutcomeDetails>(`Product/GetIntuitiveOutcomesV2/${lenderTypeId}`, getProductsOutcomesRequest).pipe(
      tap((res: OutcomeDetails) => {
        this.step = CriteriaSteps.CriteriaResult;
        this.clearBasketIntuitive();

        if (!this.outcome?.details?.length) {
          this.outcome = res;
        } else {
          this.mergeOutcomeDetails(res);
        }

        this.setBasketItems(res.contexts, true, res.autoAddedOutcomeIds);
        this.lenderCount = this.outcome.details.length;
        this.outcomeUpdated.emit(res);
      }),
    );
  }

  private getCriteriaFormResultRequests(): CriteriaFormResultRequest[] {
    const criteriaFormResults: CriteriaFormResultRequest[] = this.basketItems.map(item => {
      const context = this.criteriaIssues
        ?.flatMap(issue => issue.contexts)
        .find(context => context.criteriaContextLendingTypeId === item.criteriaContextLendingTypeId);
      const issue = this.criteriaIssues?.find(x =>
        x.contexts.find(y => y.criteriaContextLendingTypeId === item.criteriaContextLendingTypeId),
      );
      return {
        answers: item.answers,
        issue: item.issue ?? issue?.name,
        issueId: item.issueId ?? issue?.id,
        context: item.criteriaContextName,
        criteriaContextId: item.criteriaContextId,
        criteriaContextLendingTypeId: item.criteriaContextLendingTypeId ?? context?.criteriaContextLendingTypeId ?? 0,
        criteriaContextVersionId: item.criteriaContextVersionId ?? context?.criteriaContextVersionId ?? 0,
        criteriaType: item.criteriaType,
      } as CriteriaFormResultRequest;
    });

    return criteriaFormResults;
  }

  public mergeOutcomeDetails(outcomes: OutcomeDetails) {
    const outcomeDetails = this.outcome;

    outcomes.details.forEach(x => {
      const lenderOutcome = outcomeDetails.details.filter(y => y.overallResult.lender === x.overallResult.lender);
      if (lenderOutcome.length) {
        const lenderDetails = lenderOutcome[0];
        // Remove intuitive contexts
        lenderDetails.contextResults = lenderDetails.contextResults.filter(r => r.criteriaType !== CriteriaType.Intuitive);

        // merge with updated intuitive contexts
        lenderDetails.contextResults = [
          ...lenderDetails.contextResults,
          ...x.contextResults.filter(r => r.criteriaType === CriteriaType.Intuitive),
        ];

        // Get overall outcome Result
        const lenderOutcomeResult = lenderDetails.overallResult.outcomeResult;
        const propertySearchOutcomeResult = x.overallResult.outcomeResult;
        const overallOutcomeResult = this.getOverallOutcome(lenderOutcomeResult, propertySearchOutcomeResult);
        if (overallOutcomeResult) {
          lenderDetails.overallResult.outcomeResult = overallOutcomeResult.toString();
        }
      } else {
        outcomeDetails.details.push(x);
      }
    });
  }

  /**
   * Merges a historic outcome result (criteria/intuitiveOutcomes/property) into the existing outcome.
   * Unlike {@link mergeOutcomeDetails} (which only ever refreshes Intuitive-type context results and
   * discards everything else), this keeps ALL existing context results for a lender and only replaces
   * the specific contexts (matched by contextId) that the incoming result also contains - so criteria,
   * intuitiveOutcomes and property can each contribute their own auto-added/manual context results
   * without wiping out what an earlier call already added for that lender.
   */
  public mergeHistoricOutcomeDetails(outcomes: OutcomeDetails) {
    const outcomeDetails = this.outcome;

    outcomes.details.forEach(x => {
      const lenderOutcome = outcomeDetails.details.filter(y => y.overallResult.lender === x.overallResult.lender);
      if (lenderOutcome.length) {
        const lenderDetails = lenderOutcome[0];
        const incomingContextIds = new Set(x.contextResults.map(r => r.contextId));
        lenderDetails.contextResults = [
          ...lenderDetails.contextResults.filter(r => !incomingContextIds.has(r.contextId)),
          ...x.contextResults,
        ];

        const lenderOutcomeResult = lenderDetails.overallResult.outcomeResult;
        const incomingOutcomeResult = x.overallResult.outcomeResult;
        const overallOutcomeResult = this.getOverallOutcome(lenderOutcomeResult, incomingOutcomeResult);
        if (overallOutcomeResult) {
          lenderDetails.overallResult.outcomeResult = overallOutcomeResult.toString();
        }
      } else {
        outcomeDetails.details.push(x);
      }
    });
  }

  public getOverallOutcome(...results: (OutcomeResults | string | undefined)[]) {
    if (results.every(r => r === undefined)) {
      return undefined;
    }

    if (results.some(r => r === OutcomeResults.No)) {
      return OutcomeResults.No;
    }

    if (results.some(r => r === OutcomeResults.Refer || r === OutcomeResults.Unset)) {
      return OutcomeResults.Refer;
    }

    if (results.some(r => r === OutcomeResults.Yes)) {
      return OutcomeResults.Yes;
    }

    return undefined;
  }

  public getNonPropertyOutcomes(outcomes: OutcomeResponse[]): OutcomeResponse[] {
    return outcomes.map(outcome => ({
      contextResults: outcome.contextResults.filter(context => !context.isProperty),
      overallResult: outcome.overallResult,
      skipList: outcome.skipList,
    }));
  }

  public getUniqueOutcomeIds(outcomes: OutcomeResponse[]): number[] {
    return [...new Set(outcomes.flatMap(outcome => outcome.contextResults.map(context => context.outcomeId)))];
  }

  public getAdditionalOutcomes(criteriaOutcomes: OutcomeResponse[]): OutcomeResponse[] {
    const groupedByLender = new Map<string, OutcomeResponse>();

    criteriaOutcomes.forEach(outcome => {
      groupedByLender.set(outcome.overallResult.lender, {
        contextResults: outcome.contextResults.filter(context => !context.isProperty),
        overallResult: outcome.overallResult,
        skipList: outcome.skipList,
      });
    });

    return Array.from(groupedByLender.values());
  }

  public getCriteriaV2(propertySearchService: PropertySearchServiceLike, { scrollToTop = true } = {}): Observable<OutcomeDetails> {
    this.loading = true;
    return this.getOutcome(
      this.lendingTypeService.lendingType?.id ?? -1,
      this.affordabilityService.affordabilityModelRequest?.affordabilitySearchId ?? null,
      propertySearchService.propertySearchRequestViewModel,
      this.affordabilityService.affordabilityModelRequest,
      { scrollToTop },
    ).pipe(
      tap(criteriaOutcomes => {
        this.criteriaSearchId = criteriaOutcomes.criteriaSearchId;
        this.outcomeDetails.details = criteriaOutcomes.details;
        propertySearchService.splitOutPropertyCriteria(null, criteriaOutcomes.details);
        if (!this.igniteCommonDataService.lenders?.length) {
          this.igniteCommonDataService.lenders = criteriaOutcomes.details.map(x => ({
            lenderName: x.overallResult.lender,
            lenderLogoUrl: x.overallResult.lenderLogoUrl,
          }));
        }
        this.setBasketItems(criteriaOutcomes.contexts, true, criteriaOutcomes.autoAddedOutcomeIds);
      }),
    );
  }
}
